import React, { useState, useEffect } from "react";
import Pagination from "@/components/pagination";
import SearchInput from "@/components/search-input";
import API from "@/services/api";
import toast from "react-hot-toast";
import { useComponentTranslation } from "@/services/i18n";
import MessagesModal from "../components/MessagesModal";

const translations = {
  en: {
    title: "Conversations",
    total: "Total",
    no_conversations: "No conversations found",
    table: {
      participants: "Participants",
      job: "Job",
      organization: "Organization",
      last_message: "Last Message",
      status: "Status",
      updated: "Last Updated",
    },
    status: {
      active: "Active",
      archived: "Archived",
      closed: "Closed",
    },
  },
  fr: {
    title: "Conversations",
    total: "Total",
    no_conversations: "Aucune conversation trouvée",
    table: {
      participants: "Participants",
      job: "Emploi",
      organization: "Organisation",
      last_message: "Dernier message",
      status: "Statut",
      updated: "Dernière mise à jour",
    },
    status: {
      active: "Active",
      archived: "Archivée",
      closed: "Fermée",
    },
  },
};

const Conversations = () => {
  const [conversations, setConversations] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedConversation, setSelectedConversation] = useState(null);
  const limit = 25;
  const { t } = useComponentTranslation(translations);

  useEffect(() => {
    fetchConversations();
  }, [currentPage, searchTerm]);

  const fetchConversations = async () => {
    try {
      setLoading(true);
      const response = await API.post("/conversation/search", {
        page: currentPage,
        limit: limit,
        ...(searchTerm && { search: searchTerm }),
      });

      if (!response.ok) throw response;
      setConversations(response.data || []);
      setTotal(response.total || 0);
    } catch (e) {
      console.error("Error fetching conversations:", e);
      toast.error(e.code || "Error fetching conversations");
    } finally {
      setLoading(false);
    }
  };

  const handleViewMessages = (conversation) => {
    setSelectedConversation(conversation);
  };

  const getStatusBadge = (status) => {
    if (!status) {
      return <span className="inline-flex items-center px-2 py-1 rounded text-xs font-medium bg-gray-100 text-gray-800">-</span>;
    }

    const statusConfig = {
      active: { bg: "bg-green-100", text: "text-green-800", label: t("status.active") },
      archived: { bg: "bg-gray-100", text: "text-gray-800", label: t("status.archived") },
      closed: { bg: "bg-red-100", text: "text-red-800", label: t("status.closed") },
    };

    const config = statusConfig[status] || { bg: "bg-gray-100", text: "text-gray-800", label: status };
    return <span className={`inline-flex items-center px-2 py-1 rounded text-xs font-medium ${config.bg} ${config.text}`}>{config.label}</span>;
  };

  const getParticipantsDisplay = (participants) => {
    if (!participants || participants.length === 0) return "-";
    return participants.map((p) => p.user_name || "Unknown").join(" • ");
  };

  return (
    <div className="p-6">
      {/* Table Controls */}
      <div className="flex items-center justify-between mb-6">
        <SearchInput
          value={searchTerm}
          onChange={(value) => {
            setSearchTerm(value);
            setCurrentPage(1);
          }}
          className="w-64"
        />
        <div className="flex items-center gap-4">
          <div className="text-sm text-gray-600">
            {t("total")}: <span className="font-semibold text-gray-900">{total}</span>
          </div>
          <Pagination total={total} limit={limit} currentPage={currentPage} onPageChange={setCurrentPage} />
        </div>
      </div>

      {/* Table Container */}
      <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600"></div>
          </div>
        ) : conversations.length === 0 ? (
          <div className="text-center py-12">
            <p className="text-gray-500">{t("no_conversations")}</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="text-left whitespace-nowrap text-xs font-medium text-gray-600 uppercase tracking-wider bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="px-4 py-3">{t("table.participants")}</th>
                  <th className="px-4 py-3">{t("table.job")}</th>
                  <th className="px-4 py-3">{t("table.organization")}</th>
                  <th className="px-4 py-3">{t("table.last_message")}</th>
                  <th className="px-4 py-3">{t("table.status")}</th>
                  <th className="px-4 py-3">{t("table.updated")}</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {conversations.map((conversation) => {
                  const conversationId = conversation._id || conversation.id;
                  return (
                    <tr key={conversationId} onClick={() => handleViewMessages(conversation)} className="hover:bg-gray-50 transition-colors duration-150 cursor-pointer">
                      <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">
                        <div className="flex items-center gap-2">
                          {conversation.participants?.map((p, idx) => (
                            <div key={idx} className="flex items-center gap-1">
                              {p.user_avatar && <img src={p.user_avatar} alt={p.user_name} className="w-6 h-6 rounded-full" />}
                              <span className="text-xs">{p.user_name || "Unknown"}</span>
                              {p.role && (
                                <span
                                  className={`inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium ${
                                    p.role === "recruiter" ? "bg-purple-100 text-purple-800" : "bg-blue-100 text-blue-800"
                                  }`}
                                >
                                  {p.role}
                                </span>
                              )}
                            </div>
                          ))}
                        </div>
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">{conversation.job_title || "-"}</td>
                      <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">{conversation.organization_name || "-"}</td>
                      <td className="px-4 py-4 text-sm text-gray-900 max-w-xs truncate">
                        {conversation.last_message?.content ? (
                          <div>
                            <span className="font-medium text-xs">{conversation.last_message.sender_name}:</span>{" "}
                            <span className="text-xs">{conversation.last_message.content.substring(0, 50)}...</span>
                          </div>
                        ) : (
                          "-"
                        )}
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap">{getStatusBadge(conversation.status)}</td>
                      <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">
                        {conversation.updated_at ? new Date(conversation.updated_at).toLocaleString("fr-FR") : "-"}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <MessagesModal isOpen={!!selectedConversation} onClose={() => setSelectedConversation(null)} conversation={selectedConversation} />
    </div>
  );
};

export default Conversations;
